
import { supabase } from '@/lib/supabase';
import { UserProfile } from '../auth/browserAuthService';
import { debugLog, debugError } from '@/utils/debugLogger';

/**
 * Get user profile
 * @param userId User ID
 * @returns User profile or null if not found
 */
export async function getProfile(userId: string): Promise<UserProfile | null> {
  debugLog('auth', 'Getting profile for user:', userId, 'ProfileService');
  
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error || !data) {
      debugLog('auth', 'No profile found for user:', userId, 'ProfileService');
      return null;
    }
    
    debugLog('auth', 'Profile found for user:', userId, 'ProfileService');
    
    // Convert to camelCase
    return {
      id: data.id,
      userId: data.user_id,
      firstName: data.first_name,
      lastName: data.last_name,
      fullName: data.full_name,
      phone: data.phone,
      avatarUrl: data.avatar_url,
      address: data.address,
      location: data.location,
      createdAt: data.created_at
    };
    } catch (error) {
      debugError('auth', 'Error getting user profile:', error, 'ProfileService');
      return null;
    }
}

/**
 * Update user profile
 * @param userId User ID
 * @param profile Profile data to update
 * @returns Updated profile or null if failed
 */
export async function updateProfile(
  userId: string, 
  profile: Partial<UserProfile>
): Promise<UserProfile | null> {
  debugLog('auth', 'Updating profile for user:', userId, 'ProfileService');
  
  try {
    // Convert to snake_case
    const profileData = {
      first_name: profile.firstName,
      last_name: profile.lastName,
      full_name: profile.fullName,
      phone: profile.phone,
      avatar_url: profile.avatarUrl,
      address: profile.address
    };
    
    // Remove undefined values
    Object.keys(profileData).forEach(key => 
      profileData[key] === undefined && delete profileData[key]
    );
    
    const { data, error } = await supabase
      .from('user_profiles')
      .update(profileData)
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) {
      debugError('auth', 'Error updating profile:', error, 'ProfileService');
      return null;
    }
    
    debugLog('auth', 'Profile updated successfully', undefined, 'ProfileService');
    
    // Convert to camelCase
    return {
      id: data.id,
      userId: data.user_id,
      firstName: data.first_name,
      lastName: data.last_name,
      fullName: data.full_name,
      phone: data.phone,
      avatarUrl: data.avatar_url,
      address: data.address
    };
  } catch (error) {
    debugError('auth', 'Error updating profile:', error, 'ProfileService');
    return null;
  }
}

/**
 * Create or update user profile
 * @param userId User ID
 * @param profile Profile data
 * @returns Created or updated profile or null if failed
 */
export async function createOrUpdateProfile(
  userId: string,
  profile: Partial<UserProfile>
): Promise<UserProfile | null> {
  debugLog('auth', 'Creating or updating profile for user:', userId, 'ProfileService');
  
  try {
    // Check if profile exists
    const existingProfile = await getProfile(userId);
    
    if (existingProfile) {
      // Profile exists, update it
      return updateProfile(userId, profile);
    }
    
    // Profile doesn't exist, create it
    // Convert to snake_case
    const profileData = {
      user_id: userId,
      first_name: profile.firstName,
      last_name: profile.lastName,
      full_name: profile.fullName || (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : undefined),
      phone: profile.phone,
      avatar_url: profile.avatarUrl,
      address: profile.address
    };
    
    // Remove undefined values
    Object.keys(profileData).forEach(key => 
      profileData[key] === undefined && delete profileData[key]
    );
    
    const { data, error } = await supabase
      .from('user_profiles')
      .insert(profileData)
      .select()
      .single();
    
    if (error) {
      debugError('auth', 'Error creating profile:', error, 'ProfileService');
      return null;
    }
    
    debugLog('auth', 'Profile created successfully', undefined, 'ProfileService');
    
    // Convert to camelCase
    return {
      id: data.id,
      userId: data.user_id,
      firstName: data.first_name,
      lastName: data.last_name,
      fullName: data.full_name,
      phone: data.phone,
      avatarUrl: data.avatar_url,
      address: data.address
    };
  } catch (error) {
    debugError('auth', 'Error creating or updating profile:', error, 'ProfileService');
    return null;
  }
}
